import React, { useCallback, useEffect, useState } from "react";
import { Trophy, Plus, Shuffle, RotateCcw, Check, X, Trash2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { api, brl, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import TournamentBoard from "@/components/TournamentBoard";

const FORMATS = { todos: "Todos contra todos", grupos: "Grupos + mata-mata", eliminatoria: "Eliminatória (mata-mata)" };
const POINT_LABELS = { champion: "Campeão", vice: "Vice", third: "3º", fourth: "4º", quarter: "Quartas", participation: "Participação" };
const err = (e) => toast.error(formatApiError(e.response?.data?.detail));

function PlayerPick({ users, value, onChange, testid }) {
  return (
    <div className="grid grid-cols-1 gap-1.5">
      <select data-testid={`${testid}-user`} value={value.user_id} onChange={(e) => onChange({ user_id: e.target.value, name: "" })} className="w-full min-w-0 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm">
        <option value="">Login do app...</option>
        {users.map((u) => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
      </select>
      <Input data-testid={`${testid}-name`} disabled={!!value.user_id} placeholder="ou nome" value={value.name} onChange={(e) => onChange({ user_id: "", name: e.target.value })} className="rounded-xl" />
    </div>
  );
}

function CreateForm({ onCreated }) {
  const [f, setF] = useState({ name: "", date: "", price: "", description: "", categories: "" });
  const create = async () => {
    try {
      const r = await api.post("/admin/tournaments", { ...f, price: Number(f.price) || 0, categories: f.categories.split(",") });
      setF({ name: "", date: "", price: "", description: "", categories: "" });
      toast.success("Torneio criado!");
      onCreated(r.data.id);
    } catch (e) { err(e); }
  };
  return (
    <div data-testid="tour-create" className="mt-3 grid grid-cols-1 gap-2 rounded-2xl border border-slate-200 bg-white p-3 sm:grid-cols-4">
      <Input data-testid="tour-new-name" placeholder="Nome do torneio" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="rounded-xl sm:col-span-2" />
      <Input data-testid="tour-new-date" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className="rounded-xl" />
      <Input data-testid="tour-new-price" type="number" placeholder="Valor por dupla (R$)" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} className="rounded-xl" />
      <Input data-testid="tour-new-categories" placeholder="Categorias separadas por vírgula (ex: Masculino, Feminino, Misto)" value={f.categories} onChange={(e) => setF({ ...f, categories: e.target.value })} className="rounded-xl sm:col-span-2" />
      <Input data-testid="tour-new-desc" placeholder="Descrição / regras" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="rounded-xl" />
      <Button data-testid="tour-new-save" onClick={create} className="rounded-xl bg-[#1B7A45] text-white hover:bg-[#156337]"><Plus className="mr-1 h-4 w-4" /> Criar torneio</Button>
    </div>
  );
}

function CategoryAdmin({ t, cat, users, reload, canDelete }) {
  const [p1, setP1] = useState({ user_id: "", name: "" });
  const [p2, setP2] = useState({ user_id: "", name: "" });
  const [fmt, setFmt] = useState(cat.suggestion.format);
  const [groups, setGroups] = useState(cat.suggestion.groups || 2);
  useEffect(() => { setFmt(cat.format || cat.suggestion.format); setGroups(cat.groups_count || cat.suggestion.groups || 2); }, [cat.id, cat.format, cat.suggestion.format, cat.suggestion.groups, cat.groups_count]);
  const teams = t.teams.filter((x) => x.category_id === cat.id);

  const addTeam = async () => {
    try {
      await api.post(`/admin/tournaments/${t.id}/teams`, { category_id: cat.id, player1_user_id: p1.user_id, player1_name: p1.name, player2_user_id: p2.user_id, player2_name: p2.name });
      setP1({ user_id: "", name: "" }); setP2({ user_id: "", name: "" }); reload(); toast.success("Dupla adicionada!");
    } catch (e) { err(e); }
  };
  const setStatus = async (id, status) => { try { await api.patch(`/admin/tournaments/teams/${id}`, { status }); reload(); } catch (e) { err(e); } };
  const delTeam = async (id) => { if (!window.confirm("Excluir esta dupla?")) return; try { await api.delete(`/admin/tournaments/teams/${id}`); reload(); } catch (e) { err(e); } };
  const draw = async () => {
    if (cat.format && !window.confirm("Refazer o sorteio apaga todos os jogos e placares desta categoria. Continuar?")) return;
    try { await api.post(`/admin/tournaments/${t.id}/categories/${cat.id}/draw`, { format: fmt, groups: Number(groups) }); reload(); toast.success("Sorteio realizado!"); } catch (e) { err(e); }
  };
  const reset = async () => {
    if (!window.confirm("Desfazer o sorteio? Jogos e placares desta categoria serão apagados e as inscrições reabrem.")) return;
    try { await api.post(`/admin/tournaments/${t.id}/categories/${cat.id}/reset`); reload(); } catch (e) { err(e); }
  };
  const score = async (m, a, b) => { try { await api.patch(`/admin/tournaments/matches/${m.id}`, { score_a: a, score_b: b }); reload(); toast.success("Placar salvo!"); } catch (e) { err(e); } };

  return (
    <div className="space-y-4" data-testid={`tour-cat-admin-${cat.id}`}>
      <div className="rounded-2xl border border-slate-200 bg-white p-3">
        <p className="font-display text-base font-bold text-slate-900">Duplas ({teams.filter((x) => x.status === "confirmada").length} confirmadas)</p>
        <div className="mt-2 space-y-1.5">
          {teams.map((x) => (
            <div key={x.id} data-testid={`tour-adm-team-${x.id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-slate-50 px-3 py-2 text-sm">
              <span className="min-w-0 font-semibold text-slate-800">{x.player1.name} & {x.player2.name}</span>
              <div className="flex items-center gap-1.5">
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${x.status === "confirmada" ? "bg-emerald-100 text-emerald-700" : x.status === "cancelada" ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-700"}`}>{x.status === "pendente" ? `PIX pendente · ${brl(x.price)}` : x.status}</span>
                {x.status !== "confirmada" && <Button data-testid={`tour-team-confirm-${x.id}`} size="sm" onClick={() => setStatus(x.id, "confirmada")} className="h-7 rounded-full bg-[#1B7A45] px-2 text-white"><Check className="h-3.5 w-3.5" /></Button>}
                {x.status !== "cancelada" && <Button data-testid={`tour-team-cancel-${x.id}`} size="sm" variant="outline" onClick={() => setStatus(x.id, "cancelada")} className="h-7 rounded-full px-2"><X className="h-3.5 w-3.5" /></Button>}
                {canDelete && <Button data-testid={`tour-team-del-${x.id}`} size="sm" variant="outline" onClick={() => delTeam(x.id)} className="h-7 rounded-full border-red-200 px-2 text-red-600"><Trash2 className="h-3.5 w-3.5" /></Button>}
              </div>
            </div>
          ))}
        </div>
        {!cat.format && (
          <div className="mt-3 grid grid-cols-1 items-end gap-2 sm:grid-cols-[1fr_1fr_auto]">
            <PlayerPick users={users} value={p1} onChange={setP1} testid="tour-p1" />
            <PlayerPick users={users} value={p2} onChange={setP2} testid="tour-p2" />
            <Button data-testid="tour-add-team" onClick={addTeam} className="rounded-xl bg-[#E8611A] text-white hover:bg-[#CF5212]"><Plus className="mr-1 h-4 w-4" /> Dupla</Button>
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-blue-200 bg-blue-50/50 p-3">
        <p className="font-display text-base font-bold text-slate-900">Formato e sorteio</p>
        <p data-testid="tour-suggestion" className="mt-1 text-sm text-slate-600"><strong>Sugestão do app:</strong> {FORMATS[cat.suggestion.format]}{cat.suggestion.format === "grupos" ? ` com ${cat.suggestion.groups} grupos` : ""}. {cat.suggestion.reason}</p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <select data-testid="tour-format" value={fmt} onChange={(e) => setFmt(e.target.value)} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm">
            {Object.entries(FORMATS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          {fmt === "grupos" && <label className="flex items-center gap-1.5 text-sm text-slate-600">Grupos <Input data-testid="tour-groups" type="number" min="2" value={groups} onChange={(e) => setGroups(e.target.value)} className="w-20 rounded-xl bg-white" /></label>}
          <Button data-testid="tour-draw" onClick={draw} className="rounded-xl bg-[#2563eb] text-white hover:bg-[#1d4ed8]"><Shuffle className="mr-1 h-4 w-4" /> {cat.format ? "Refazer sorteio" : "Sortear automaticamente"}</Button>
          {cat.format && <Button data-testid="tour-reset" onClick={reset} variant="outline" className="rounded-xl"><RotateCcw className="mr-1 h-4 w-4" /> Desfazer sorteio</Button>}
        </div>
        {cat.format && <p className="mt-2 text-xs text-slate-500">Formato atual: <strong>{FORMATS[cat.format]}</strong>. Preencha os placares abaixo — a tabela pública atualiza em tempo real e o mata-mata avança sozinho.</p>}
      </div>

      <TournamentBoard data={t} cat={cat} editable onScore={score} />
    </div>
  );
}

export default function TournamentManager() {
  const { user } = useAuth();
  const [enabled, setEnabled] = useState(false);
  const [list, setList] = useState([]);
  const [tid, setTid] = useState(null);
  const [data, setData] = useState(null);
  const [catId, setCatId] = useState(null);
  const [users, setUsers] = useState([]);
  const [newCat, setNewCat] = useState("");
  const [pts, setPts] = useState({});
  const canDelete = (user?.perms || []).includes("excluir");

  const loadList = useCallback(() => api.get("/tournaments").then((r) => { setList(r.data); setTid((x) => x || r.data[0]?.id || null); }).catch(() => {}), []);
  const load = useCallback(() => { if (tid) api.get(`/tournaments/${tid}`).then((r) => { setData(r.data); setPts(r.data.points_table || {}); setCatId((c) => (r.data.categories.some((x) => x.id === c) ? c : r.data.categories[0]?.id)); }).catch(() => {}); }, [tid]);
  useEffect(() => {
    loadList();
    api.get("/settings").then((r) => setEnabled(!!r.data.tournament_enabled)).catch(() => {});
    api.get("/admin/users").then((r) => setUsers([...r.data].filter((u) => u.name).sort((a, b) => a.name.localeCompare(b.name, "pt-BR")))).catch(() => {});
  }, [loadList]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (v) => { try { await api.post("/admin/settings/tournament-toggle", { enabled: v }); setEnabled(v); toast.success(v ? "Botão Torneio ativado no menu." : "Botão Torneio desativado."); } catch (e) { err(e); } };
  const patchT = async (body, msg) => { try { await api.patch(`/admin/tournaments/${tid}`, body); load(); loadList(); if (msg) toast.success(msg); } catch (e) { err(e); } };
  const delT = async () => { if (!window.confirm("Excluir este torneio com duplas, jogos e pontos do ranking?")) return; try { await api.delete(`/admin/tournaments/${tid}`); setTid(null); setData(null); loadList(); } catch (e) { err(e); } };
  const cat = data?.categories.find((c) => c.id === catId);

  return (
    <div className="mt-4" data-testid="tournament-manager">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-white p-4">
        <div className="flex items-center gap-2"><Trophy className="h-5 w-5 text-amber-500" /><p className="font-display text-lg font-bold text-slate-900">Botão "Torneio" no menu</p></div>
        <label className="flex items-center gap-2 text-sm font-semibold text-slate-600">{enabled ? "Ativado" : "Desativado"}<Switch data-testid="tour-toggle" checked={enabled} onCheckedChange={toggle} /></label>
      </div>

      <h2 className="mt-6 font-display text-xl font-bold text-slate-900">Novo torneio</h2>
      <CreateForm onCreated={(id) => { setTid(id); loadList(); }} />

      {list.length > 0 && (
        <div className="mt-6 flex flex-wrap gap-2">
          {list.map((x) => <button key={x.id} data-testid={`tour-pick-${x.id}`} onClick={() => { setTid(x.id); setCatId(null); }} className={`rounded-full px-4 py-2 text-sm font-bold ${x.id === tid ? "bg-[#0b1610] text-white" : "bg-white text-slate-600 hover:bg-slate-100"}`}>{x.name}</button>)}
        </div>
      )}

      {data && (
        <div className="mt-4 space-y-4">
          <div className="grid grid-cols-1 gap-2 rounded-2xl border border-slate-200 bg-white p-3 sm:grid-cols-[1fr_auto_auto]">
            <div>
              <p className="font-display text-xl font-black text-slate-900">{data.name}</p>
              <p className="text-xs text-slate-500">{data.date ? data.date.split("-").reverse().join("/") : "sem data"} · {brl(data.price)} por dupla</p>
            </div>
            <select data-testid="tour-status" value={data.status} onChange={(e) => patchT({ status: e.target.value }, "Status atualizado.")} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm">
              <option value="inscricoes">Inscrições abertas</option><option value="em_andamento">Em andamento</option><option value="finalizado">Finalizado</option>
            </select>
            {canDelete && <Button data-testid="tour-delete" onClick={delT} variant="outline" className="rounded-xl border-red-200 bg-red-50 text-red-600"><Trash2 className="h-4 w-4" /></Button>}
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-3">
            <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Pontos do ranking por colocação (por jogador)</p>
            <div className="mt-2 grid grid-cols-3 gap-2 sm:grid-cols-7">
              {Object.entries(POINT_LABELS).map(([k, l]) => <label key={k} className="text-[11px] font-semibold text-slate-500">{l}<Input data-testid={`tour-pts-${k}`} type="number" value={pts[k] ?? ""} onChange={(e) => setPts({ ...pts, [k]: e.target.value })} className="rounded-lg" /></label>)}
              <Button data-testid="tour-pts-save" onClick={() => patchT({ points_table: pts }, "Pontuação salva. Vale para resultados novos.")} className="self-end rounded-lg bg-[#1B7A45] text-white hover:bg-[#156337]"><Save className="h-4 w-4" /></Button>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {data.categories.map((c) => <button key={c.id} data-testid={`tour-adm-cat-${c.id}`} onClick={() => setCatId(c.id)} className={`rounded-full px-4 py-2 text-sm font-bold ${c.id === catId ? "bg-[#1B7A45] text-white" : "bg-white text-slate-600 hover:bg-slate-100"}`}>{c.name}</button>)}
            <Input data-testid="tour-add-cat-input" placeholder="Nova categoria" value={newCat} onChange={(e) => setNewCat(e.target.value)} className="w-40 rounded-full" />
            <Button data-testid="tour-add-cat" size="sm" onClick={() => { patchT({ add_category: newCat }, "Categoria adicionada."); setNewCat(""); }} className="rounded-full bg-slate-800 text-white"><Plus className="h-4 w-4" /></Button>
          </div>

          {cat && <CategoryAdmin key={cat.id} t={data} cat={cat} users={users} reload={load} canDelete={canDelete} />}
        </div>
      )}
    </div>
  );
}
